import { useMutation } from '@tanstack/react-query';
import {
  Action,
  Card,
  ConfirmModal,
  Disclosure,
  Loading,
  Modal,
  OrderLineItem,
  Page,
  RelativeDateTime,
  SimpleSummary,
} from 'corso-admin-ui';
import {
  CorsoClaimType,
  CrewMerchantUi,
  maxPolicyEligibilityDays,
} from 'corso-types';
import { differenceInDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import api from '~/api';
import DescriptionList, { Description } from '~/components/DescriptionList';

import { formatter } from 'corso-web-lib';
import PageStatus from '~/components/PageStatus';
import { useAutomations } from '~/features/settings/hooks/useAutomations';
import { usePathParams } from '~/hooks/usePathParams';
import { useReturnSettings } from '~/hooks/useReturnSettings';
import { useSearchParam } from '~/hooks/useSearchParam';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import ClaimCreateForm from './components/ClaimCreateForm';
import ClaimTypeSelection from './components/ClaimTypeSelection';
import OrderLookup from './components/OrderLookup';

const useFetchOrder = () => {
  const storeId = useStoreId();

  return useMutation({
    mutationFn: api.store(storeId).order.get,
  });
};

const getWarrantyEligible = (lastEligibleDate: string) =>
  maxPolicyEligibilityDays ===
  differenceInDays(new Date(lastEligibleDate), new Date())
    ? 'Lifetime'
    : formatter.date(lastEligibleDate);

function EligibleLineItem({
  storeOrderId,
  lineItem,
  automations,
  onOrderUpdate,
}: {
  storeOrderId: number;
  lineItem: CrewMerchantUi.CrewOrder['lineItems'][0];
  automations: Awaited<ReturnType<typeof useAutomations>>['data'];
  onOrderUpdate: () => Promise<void>;
}) {
  const {
    return: returns,
    warranty,
    automationMadeIneligibleOn,
    productTagsAtPurchase,
    ineligibleLineItemFootnote,
  } = lineItem;
  const storeId = useStoreId();

  const [showClearIneligibleOn, setShowClearIneligibleOn] = useState(false);

  const { mutateAsync: updateLineItem, isPending } = useMutation({
    mutationFn: () =>
      api
        .store(storeId)
        .order.updateLineItem(`${storeOrderId}`, `${lineItem.id}`, {
          madeIneligibleOn: automationMadeIneligibleOn
            ? null
            : new Date().toISOString(),
        }),
  });

  const handleResetEligibility = async () => {
    try {
      await updateLineItem();
      await onOrderUpdate();
      setShowClearIneligibleOn(false);
    } catch (error) {
      console.error('Error resetting eligibility:', error);
    }
  };

  const gridDescription: Description[] = [];

  const { exchange, giftCard, refund } = returns;

  const eligibleFor = [] as string[];
  if (giftCard.isEligible && !automationMadeIneligibleOn)
    eligibleFor.push('Gift Card');
  if (refund.isEligible && !automationMadeIneligibleOn)
    eligibleFor.push('Refund');
  if (exchange.isEligible && !automationMadeIneligibleOn)
    eligibleFor.push('Exchange');
  // making an item ineligible only blocks returns; warranty eligibility is untouched
  if (warranty.isEligible) eligibleFor.push('Warranty');

  const modifyWindowRule = automations.find(
    (rule) => rule.id === lineItem.eligibilityStoreRuleId,
  );

  const eligibility = eligibleFor.length ? (
    <span>Eligible for: {eligibleFor.join(', ')}</span>
  ) : (
    <span className="text-red-600">
      {ineligibleLineItemFootnote ?? 'Not Eligible'}
    </span>
  );

  gridDescription.push({
    term: 'Exchange',
    details:
      exchange.lastEligibleDate && !automationMadeIneligibleOn
        ? formatter.date(exchange.lastEligibleDate)
        : 'Not Offered',
  });

  gridDescription.push({
    term: 'Refund',
    details:
      refund.lastEligibleDate && !automationMadeIneligibleOn
        ? formatter.date(refund.lastEligibleDate)
        : 'Not Offered',
  });

  gridDescription.push({
    term: 'Gift Card',
    details:
      giftCard.lastEligibleDate && !automationMadeIneligibleOn
        ? formatter.date(giftCard.lastEligibleDate)
        : 'Not Offered',
  });

  gridDescription.push({
    term: 'Warranty',
    details: warranty.lastEligibleDate
      ? getWarrantyEligible(warranty.lastEligibleDate)
      : 'Not Offered',
  });

  const listDescriptions: Description[] = [];

  listDescriptions.push({
    term: 'Is Ineligible Due to Policy Enforcement',
    details: (
      <div className="flex items-center justify-between">
        {automationMadeIneligibleOn ? 'Yes' : 'No'}
        <Action
          variant={automationMadeIneligibleOn ? 'default' : 'caution'}
          className="text-xs"
          accessibilityLabel="Clear Ineligible On"
          onClick={() => {
            setShowClearIneligibleOn(true);
          }}
        >
          {automationMadeIneligibleOn ? 'Reset Eligibility' : 'Make Ineligible'}
        </Action>
      </div>
    ),
  });

  if (modifyWindowRule) {
    listDescriptions.push({
      term: 'Modify Claim Window Automation',
      details: modifyWindowRule.name,
    });
  }

  listDescriptions.push({
    term: 'Product Tags At Purchase',
    details: productTagsAtPurchase?.length
      ? productTagsAtPurchase.join(', ')
      : 'None',
  });

  const prompts = {
    resetEligibility:
      'Are you sure you would like to reset the eligibility for this line item? This will allow a customer to create a claim for this line item.',
    makeIneligible:
      'Are you sure you would like to make this line item ineligible? This will prevent a customer from creating a claim for this line item.',
  };

  return (
    <>
      <ConfirmModal
        onConfirm={() => {
          handleResetEligibility().catch(console.error);
        }}
        variant={automationMadeIneligibleOn ? 'primary' : 'caution'}
        title="Edit Line Item Eligibility"
        prompt={
          automationMadeIneligibleOn
            ? prompts.resetEligibility
            : prompts.makeIneligible
        }
        confirmText="Confirm"
        cancelText="Cancel"
        show={showClearIneligibleOn}
        loading={isPending}
        onCancel={() => setShowClearIneligibleOn(false)}
      />
      <Disclosure
        renderSummary={() => (
          <SimpleSummary>
            <OrderLineItem
              title={lineItem.name}
              src={lineItem.imgUrl}
              quantity={lineItem.currentQuantity}
              options={lineItem.optionsFromPlatform}
            >
              <OrderLineItem.Detail>{eligibility}</OrderLineItem.Detail>
            </OrderLineItem>
          </SimpleSummary>
        )}
      >
        <div className="flex flex-col gap-2 p-3 lg:gap-4 lg:p-4">
          <DescriptionList descriptions={listDescriptions} layoutType="list" />

          <DescriptionList descriptions={gridDescription} />
        </div>
      </Disclosure>
    </>
  );
}

function EligibilityOverview({
  order,
  show,
  close,
  onOrderUpdate,
}: {
  order: CrewMerchantUi.CrewOrder;
  show: boolean;
  close: () => void;
  onOrderUpdate: () => Promise<void>;
}) {
  const { lineItems, orderNo, createdOn } = order;

  const { data: automations } = useAutomations();

  const descriptions = [];

  descriptions.push({
    details: orderNo,
    term: 'Order Number',
  });

  descriptions.push({
    details: <RelativeDateTime dateTime={createdOn} />,
    term: 'Order Date',
  });

  const sortedLineItems = lineItems.sort((a, b) => a.id - b.id);

  return (
    <Modal show={show} onClose={close} title="Order Eligibility">
      <DescriptionList descriptions={descriptions} />
      <p className="text-lg">Line Items</p>
      {sortedLineItems.map((lineItem) => (
        <EligibleLineItem
          storeOrderId={order.id}
          key={lineItem.id}
          lineItem={lineItem}
          automations={automations}
          onOrderUpdate={onOrderUpdate}
        />
      ))}
    </Modal>
  );
}

const paramsSchema = z.object({
  storeId: z.string(),
  orderIdFromPlatform: z.string().optional(),
});

export default function OrderLookupPage() {
  const [order, setOrder] = useState<CrewMerchantUi.CrewOrder | null>(null);
  const [claimType, setClaimType] = useState<CorsoClaimType | null>(null);
  const [showEligibility, setShowEligibility] = useState(false);
  const returnTo = useSearchParam('returnTo');
  const navigate = useNavigate();

  const { data: returnSettings } = useReturnSettings();
  const isGiftReturnEnabled = returnSettings?.returns.isGiftReturnEnabled;

  const storeId = useStoreId();
  const toast = useToast();

  const { orderIdFromPlatform } = usePathParams(paramsSchema);
  const { mutateAsync: fetchOrder, isPending } = useFetchOrder();

  const handleOrderUpdate = async () => {
    if (orderIdFromPlatform) {
      const fetchedOrder = await fetchOrder({
        idFromPlatform: orderIdFromPlatform,
      });
      setOrder(fetchedOrder);
    }
  };

  useEffect(() => {
    if (orderIdFromPlatform) {
      fetchOrder({ idFromPlatform: orderIdFromPlatform })
        .then((fetchedOrder) => {
          setOrder(fetchedOrder);
          setClaimType(null);
        })
        .catch((error) => {
          console.error('Error fetching order:', error);
          setOrder(null);
          setClaimType(null);
          // ensure if we fail to find the order, or we change stores, we clear the orderIdFromPlatform
          navigate(returnTo ?? `/${storeId}/orders/lookup`);
        });
    }
  }, [orderIdFromPlatform, fetchOrder, storeId, returnTo, navigate]);

  if (isPending)
    return (
      <>
        <Loading />
        <PageStatus.Loading title="Loading Order" />
      </>
    );

  if (!order) return <OrderLookup onOrderLookup={setOrder} />;

  const handleCopyGiftReturnLink = () => {
    navigator.clipboard
      .writeText(order.startGiftReturnUrl ?? '')
      .catch((error) => console.error(error))
      .finally(() => toast.show('Gift Return URL copied to clipboard'));
  };

  return (
    <Page
      title="Order Overview"
      size="narrow"
      backAction={{
        accessibilityLabel: 'Back to Order Lookup',
        onAction() {
          setOrder(null);
          setClaimType(null);
          navigate(returnTo ?? `/${storeId}/orders/lookup`);
        },
      }}
      primaryAction={{
        content: 'View Eligibility',
        onAction: () => setShowEligibility(true),
      }}
      secondaryActions={[
        {
          id: 'copy-gift-return-link',
          content: 'Gift Return URL',
          icon: 'gift' as const,
          hidden: !order.startGiftReturnUrl || !isGiftReturnEnabled,
          onAction: () => handleCopyGiftReturnLink(),
        },
      ]}
    >
      <Card>
        <Card.Heading>Order {order.orderNo}</Card.Heading>
        <ClaimTypeSelection onClaimTypeSelect={setClaimType} />
        {claimType && (
          <ClaimCreateForm
            // resets the form when the order or claim type changes
            key={`${order.id}-${claimType}`}
            order={order}
            claimType={claimType}
            onCancel={() => {
              setOrder(null);
              setClaimType(null);
            }}
          />
        )}
        <EligibilityOverview
          order={order}
          show={showEligibility}
          close={() => setShowEligibility(false)}
          onOrderUpdate={handleOrderUpdate}
        />
      </Card>
    </Page>
  );
}
